import { Button } from '@/shared/ui/button.tsx'
import { Plus, X } from 'lucide-react'
import { SetStateAction, useState } from 'react'
import { Platform } from '../types'
import { cn } from '@/lib/utils'

type Props = {
  currentPlatform: { name: string, url: string },
  platformOptions: Platform[],
  selectedPlatforms: Platform[],
  setSelectedPlatforms: React.Dispatch<SetStateAction<Platform[]>>
}

const MultiPlatformSelect = ({ currentPlatform, platformOptions, setSelectedPlatforms, selectedPlatforms }: Props) => {

  const [isExpanded, setIsExpanded] = useState(false)

  const togglePlatform = (platform: Platform) => {
    setSelectedPlatforms((prev) => {
      const isSelected = prev.some((p) => p.id === platform.id)
      if (isSelected) {
        return prev.filter((p) => p.id !== platform.id)
      } else {
        return [...prev, platform]
      }
    })
  }

  const removePlatform = (platformId: string) => {
    setSelectedPlatforms((prev) => prev.filter((p) => p.name !== platformId))
  }

  const unselectedPlatforms = platformOptions.filter(
    item1 => !selectedPlatforms.some(item2 => item2.name === item1.name)
  ).filter(data => data.name !== currentPlatform.name)

  return (
    <div className="w-full max-w-6xl flex justify-center">
      {/* Main selector container with dynamic width */}
      <div className="transition-all duration-500 ease-in-out overflow-hidden w-fit min-w-fit">
        <div className="flex items-center gap-4 whitespace-nowrap">
          <span className="text-sm font-medium flex-shrink-0 font-poppins">To be published on</span>

          <div
            className={`size-6 rounded-xl flex items-center justify-center text-lg cursor-pointer transition-transform hover:scale-105`}
          >
            <img src={currentPlatform.url} className='object-cover' />
          </div>
          {/* Selected platforms */}
          {selectedPlatforms.map((platform) => (
            <div key={platform.id} className="relative group flex-shrink-0">
              <div
                className={`size-5 rounded-xl flex items-center justify-center text-lg cursor-pointer transition-transform hover:scale-105`}
              >
                <img src={platform.url} className='object-cover' />
              </div>
              <button
                onClick={() => removePlatform(platform.name)}
                className="absolute -top-1 -right-1 size-3 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}

          {/* Available platforms (shown when expanded) */}
          <div
            className={`flex items-center gap-3 transition-all duration-500 ease-in-out ${isExpanded ? "opacity-100 translate-x-0 max-w-none" : "opacity-0 -translate-x-4 max-w-0"
              }`}
          >
            {isExpanded && (
              <>
                {/* Separator line */}
                <div className="w-px h-8 bg-gray-600 flex-shrink-0"></div>

                <div className='flex items-center gap-4'>
                  {unselectedPlatforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => togglePlatform(platform)}
                      className={`size-5 rounded-xl flex items-center justify-center text-white text-lg transition-all duration-200 hover:scale-110 hover:shadow-lg flex-shrink-0`}
                      title={`Add ${platform.name}`}
                    >
                      <img src={platform.url} className='object-cover' />
                    </button>
                  ))}
                </div>

                {unselectedPlatforms.length === 0 && (
                  <span className="text-gray-600 font-poppins text-sm italic ml-2 flex-shrink-0">All platforms selected</span>
                )}
              </>
            )}
          </div>

          {/* Plus/Close button */}
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            size="icon"
            title="Other Platform choices"
            className={cn("size-7 rounded-full transition-all duration-200 flex-shrink-0",
              !isExpanded && "-ml-4"
            )}
          >
            <Plus className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-45" : ""}`} />
          </Button>
        </div>
      </div>
    </div>

  )
}

export default MultiPlatformSelect
